import type { RefObject } from 'react'
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import _ from 'lodash'
import {
  Box,
  VStack,
  FormControl as ChakraFormControl,
  FormErrorMessage as ChakraFormErrorMessage,
  Input as ChakraInput,
} from '@chakra-ui/react'
import type { OnChangeValue } from 'react-select'

import { IKReactSelectCreatable } from '../../../../ui-kit'
import type { InputFormProps as Props } from '../../../interfaces'
import { formatTags, getFieldsMapping } from '../../../../utils/mediaLibrary'
import OperatorMenu from '../OperatorMenu'
import { ClientTagResponse } from '../../../interfaces'
import useFetch, { CachePolicies } from 'use-http'
import {
  DEFAULT_CLIENT_TAG_LIMIT,
  DEFAULT_CLIENT_TAG_SKIP,
} from '../../../constants'
import { useDebounce } from '../../../hooks/useDebounce'
import { UserContext } from '../../../providers'
import { useTranslation } from 'react-i18next'
import useToast from '../../../hooks/useToast'

const TagsInputForm = (props: Props): React.ReactElement => {
  const {
    searchQueryJson,
    validationObject,
    groupId,
    filterId,
    initialFocusRef,
    setSearchQueryJson,
  } = props

  const { t } = useTranslation()
  const { user } = useContext(UserContext)
  const toast = useToast()
  const [suggestedTags, setSuggestedTags] = useState<ClientTagResponse[]>([])
  const [search, setSearch] = useState<string>()
  const debouncedSearchTerm = useDebounce(search, 500)

  const { request } = useFetch({
    cachePolicy: CachePolicies.NO_CACHE,
  })

  const [dirty, setDirty] = useState<boolean>(false)

  const expression = useMemo(
    () => searchQueryJson.filterGroups[groupId]?.expressions[filterId],
    [searchQueryJson, groupId, filterId],
  )

  const error: string | null = useMemo(
    () => validationObject.errors[groupId][filterId],
    [validationObject, groupId, filterId],
  )

  const handleOperatorChange = useCallback(
    (newOperator: string) => {
      const newSearchQueryJson = _.cloneDeep(searchQueryJson)
      newSearchQueryJson.filterGroups[groupId].expressions[filterId].operator =
        newOperator
      newSearchQueryJson.filterGroups[groupId].expressions[filterId].value =
        getFieldsMapping({ fieldName: expression.fieldName }).getInitialValue(
          newOperator,
        )
      setSearchQueryJson(newSearchQueryJson)
    },
    [searchQueryJson, groupId, filterId],
  )

  const handleValueChange = useCallback(
    (newValue: string | string[]) => {
      const newSearchQueryJson = _.cloneDeep(searchQueryJson)
      newSearchQueryJson.filterGroups[groupId].expressions[filterId].value =
        newValue
      setSearchQueryJson(newSearchQueryJson)
    },
    [searchQueryJson, groupId, filterId],
  )

  useEffect(() => {
    initialFocusRef.current?.focus()
  }, [initialFocusRef, expression.operator])


  const getSuggestedTags = useCallback(async (debouncedSearchTerm) => {
    if (user?.clientNumber) {
      let url = `/api/clients/${user?.clientNumber}/client-tags?limit=${DEFAULT_CLIENT_TAG_LIMIT}&skip=${DEFAULT_CLIENT_TAG_SKIP}`
      if (debouncedSearchTerm) url = `${url}&label=${debouncedSearchTerm}`

      const response = await request.get(url)

      if (request.error) {
        toast({
          description: t(
            'error_fetching_data',
            'Error in fetching data. Try refreshing the page.',
          ),
          status: 'error',
          isClosable: true,
          duration: 5000,
          position: 'top-right',
          variant: 'outline',
        })
      } else if (response?.tagsData && Array.isArray(response.tagsData)) {
        setSuggestedTags(response.tagsData)
      }
    }
  }, [])

  useEffect(() => {
    getSuggestedTags(debouncedSearchTerm)
  }, [debouncedSearchTerm, user])

  const tagsOptions = useMemo(() => {
    const selectedValues = Array.isArray(expression.value) ? expression.value : []
    return suggestedTags
      .filter(data => !selectedValues.includes(data.label))
      .map(data => ({
        label: data.label,
        value: data.name,
      }))
  }, [suggestedTags, expression.value])

  return (
    <ChakraFormControl width="full" isInvalid={error !== null && dirty}>
      <VStack width="full" spacing="0.5em">
        <VStack alignItems="stretch" width="full" spacing="0.5em">
          <OperatorMenu
            width="full"
            expression={expression}
            handleOperatorChange={handleOperatorChange}
          />
          {['HAS'].includes(expression.operator) && (
            <ChakraInput
              ref={initialFocusRef as RefObject<HTMLInputElement>}
              width="full"
              id={expression.fieldName}
              value={expression.value as string}
              onChange={e => {
                handleValueChange(e.target.value)
                setDirty(true)
              }}
              bgColor="white"
            />
          )}

          {['IS_ONE_OF', 'IS_NONE_OF'].includes(expression.operator) &&
            Array.isArray(expression.value) && (
              <Box width="full">
                <IKReactSelectCreatable
                  ref={initialFocusRef}
                  variant="compact"
                  placeholder="Add tags"
                  maxMenuHeight={130}
                  isMulti
                  value={
                    expression.value.map(val => ({
                      value: val as string,
                      label: val as string,
                    })) ?? []
                  }
                  options={tagsOptions}
                  inputValue={search}
                  onInputChange={value => setSearch(value)}
                  skipExactMatch={true}
                  onChange={(
                    arr: OnChangeValue<
                      {
                        label: string
                        value: string
                      },
                      true
                    >,
                  ) => {
                    const newVal = formatTags(arr).map(val => val.label)
                    handleValueChange(newVal)
                    setDirty(true)
                  }}
                />
              </Box>
            )}
        </VStack>

        <ChakraFormErrorMessage width="full">{error}</ChakraFormErrorMessage>
      </VStack>
    </ChakraFormControl>
  )
}

export default TagsInputForm
